import React, { useEffect, useMemo, useState } from 'react'

const TABS = [
  { id: 'vehicle-info', label: 'Vehicle Info' },
  { id: 'vin-ticket-history', label: 'VIN Ticket History' },
  { id: 'rider-ticket-history', label: 'Rider Ticket History' },
  { id: 'swap-history', label: 'Swap History' },
]

const formatState = (value) => {
  if (!value) return '—'
  return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
}

const formatStatusClass = (value) => {
  const normalized = String(value || '').toLowerCase()
  if (normalized === 'success' || normalized === 'resolved' || normalized === 'closed') return 'success'
  if (normalized === 'fail') return 'fail'
  if (normalized === 're_opened') return 'reopened'
  return 'progress'
}

const formatTicketStatus = (value) => {
  if (!value) return '—'
  const map = {
    in_progress: 'In Progress',
    resolved: 'Resolved',
    closed: 'Closed',
    re_opened: 'Re-Opened',
  }
  return map[value] || formatState(value)
}

const formatSwapType = (value) => {
  if (!value) return '—'
  return value.charAt(0).toUpperCase() + value.slice(1)
}

const parseDaysAgo = (label) => {
  if (!label || typeof label !== 'string') return null
  const parsed = new Date(label)
  if (Number.isNaN(parsed.getTime())) return null
  const now = new Date()
  return Math.max(0, Math.floor((now.getTime() - parsed.getTime()) / (24 * 60 * 60 * 1000)))
}

const formatDateTime = (value, fallbackDate, fallbackTime) => {
  if (value) {
    const date = new Date(value)
    if (!Number.isNaN(date.getTime())) {
      return {
        date: date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
        time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      }
    }
  }
  return { date: fallbackDate || '—', time: fallbackTime || '—' }
}

const StatCard = ({ value, label, accent }) => (
  <div className="v360-stat-card">
    <div className={`v360-stat-value ${accent}`}>{value ?? '—'}</div>
    <div className="v360-stat-label">{label}</div>
  </div>
)

const DetailRow = ({ label, value, mono }) => (
  <div className="v360-detail-row">
    <span className="v360-detail-label">{label}</span>
    <span className={`v360-detail-value ${mono ? ' mono' : ''}`}>{value || '—'}</span>
  </div>
)

export default function Vehicle360Page({
  fetchJson,
  selectedVehicle,
  setSelectedVehicle,
  setCurrentPage,
  setSelectedTicket,
}) {
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(false)
  const [searching, setSearching] = useState(false)
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState('vehicle-info')
  const [vehicleQuery, setVehicleQuery] = useState('')
  const [mobileQuery, setMobileQuery] = useState('')
  const [loadedVehicleId, setLoadedVehicleId] = useState('')

  const selectedVehicleId = selectedVehicle?.vehicle_id || selectedVehicle?.id || loadedVehicleId

  const loadVehicle = (vehicleId) => {
    if (!vehicleId || !fetchJson) return
    setLoading(true)
    setError('')
    fetchJson(`/api/vehicles/vehicle/${vehicleId}/`)
      .then((data) => {
        setDetail(data)
        setLoadedVehicleId(vehicleId)
        setActiveTab('vehicle-info')
      })
      .catch(() => {
        setDetail(null)
        setError('Failed to load Vehicle 360 details.')
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (selectedVehicleId) loadVehicle(selectedVehicleId)
  }, [selectedVehicleId])

  const handleSearch = () => {
    const query = vehicleQuery.trim() || mobileQuery.trim()
    if (!query || !fetchJson) return
    setSearching(true)
    setError('')
    fetchJson(`/api/vehicles/search-bar?q=${encodeURIComponent(query)}`)
      .then((data) => {
        const vehicleDetails = data?.vehicle_details
        if (!vehicleDetails?.id) {
          setDetail(null)
          setError('No vehicle found for that VIN, registration number, or mobile number.')
          return
        }
        setSelectedVehicle?.({
          vehicle_id: vehicleDetails.id,
          vin_number: vehicleDetails.vin,
          registration_number: vehicleDetails.registration_number,
        })
        loadVehicle(vehicleDetails.id)
      })
      .catch(() => {
        setDetail(null)
        setError('Vehicle search failed.')
      })
      .finally(() => setSearching(false))
  }

  const info = detail?.vehicle_info || {}
  const stats = detail?.ticket_stats || {}
  const vinTickets = detail?.vin_ticket_history || []
  const riderTickets = detail?.rider_ticket_history || []
  const swapHistory = detail?.swap_history || []

  const serviceValue = useMemo(() => {
    const latest = vinTickets[0]?.created_at || vinTickets[0]?.created_date
    const days = parseDaysAgo(latest)
    return days === null ? '—' : `${days}d`
  }, [vinTickets])

  const batteryHealth = Math.max(0, Math.min(100, Number(info.battery_health_percentage || 0)))
  const summaryRegistration = selectedVehicle?.registration_number || info.registration_number || '—'
  const summaryVin = selectedVehicle?.vin_number || info.vin || '—'
  const summaryModel = selectedVehicle?.model || info.model || ''
  const summaryOem = selectedVehicle?.oem || info.oem || ''
  const summaryAccount = selectedVehicle?.account_type || selectedVehicle?.account || info.account || ''
  const summaryState = selectedVehicle?.vehicle_state || selectedVehicle?.state || info.state || 'active'
  const openTickets = stats.tickets_opened ?? selectedVehicle?.number_of_open_tickets ?? '—'
  const totalTickets = stats.total_tickets ?? selectedVehicle?.previous_ticket_numbers?.length ?? vinTickets.length ?? '—'

  const openTicket = (ticket) => {
    setSelectedTicket?.({
      ticket_id: ticket.ticket_id,
      ticket_number: ticket.ticket_number,
    })
    setCurrentPage?.('ticket-detail')
  }

  return (
    <div className="v360-page">
      <div className="v360-hero">
        <div>
          <p className="v360-subtitle">Search by VIN, registration number, or rider mobile to open the full vehicle context.</p>
        </div>

        <div className="v360-search-row">
          <input
            className="v360-search-input"
            placeholder="VIN or registration number"
            value={vehicleQuery}
            onChange={(e) => setVehicleQuery(e.target.value)}
          />
          <input
            className="v360-search-input"
            placeholder="Mobile No. (e.g. 9876543210)"
            value={mobileQuery}
            onChange={(e) => setMobileQuery(e.target.value)}
          />
          <button className="v360-search-btn" onClick={handleSearch} disabled={searching}>
            {searching ? 'Searching…' : 'Search'}
          </button>
        </div>

        <div className="v360-search-hint">Try: IN-EV-2024-00847, DL01AB1234, or 9876543210</div>
      </div>

      {error && <div className="v360-feedback error">{error}</div>}
      {loading && <div className="v360-feedback">Loading Vehicle 360…</div>}

      {!loading && !detail && !error && (
        <div className="v360-empty">
          <div className="v360-empty-title">No vehicle selected</div>
          <div className="v360-empty-copy">Use the search above or open a vehicle from the global search dropdown.</div>
        </div>
      )}

      {!loading && detail && (
        <>
          <div className="v360-summary-card">
            <div className="v360-summary-main">
              <div className="v360-vehicle-avatar">🛵</div>
              <div>
                <div className="v360-summary-id">
                  {summaryRegistration}
                  <span>{summaryVin}</span>
                </div>
                <div className="v360-summary-meta">
                  {[summaryModel, summaryOem, formatState(summaryAccount)].filter(Boolean).join(' · ')}
                  <span className="v360-live-state">● {formatState(summaryState)}</span>
                </div>
              </div>
            </div>
            <div className="v360-summary-stats">
              <StatCard value={openTickets} label="Open" accent="warning" />
              <StatCard value={totalTickets} label="Total" accent="dark" />
              <StatCard value={serviceValue} label="Last SVC" accent="muted" />
            </div>
          </div>

          <div className="v360-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                className={`v360-tab${activeTab === tab.id ? ' active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'vehicle-info' && (
            <div className="v360-grid">
              <section className="v360-panel">
                <h3>Vehicle Details</h3>
                <DetailRow label="VIN" value={info.vin} mono />
                <DetailRow label="Reg No." value={info.registration_number} mono />
                <DetailRow label="Model" value={info.model} />
                <DetailRow label="Type" value={info.type} />
                <DetailRow label="OEM" value={info.oem} />
                <DetailRow label="Recharge status" value={info.recharge_status} mono />

                <DetailRow label="Account" value={formatState(info.account)} />
                <DetailRow label="Partner Name" value={info.partner_name} mono />
                <DetailRow label="Partner ID" value={info.partner_id} mono />
                <DetailRow label="Station" value={info.station || selectedVehicle?.station || '—'} mono />
                <DetailRow label="Battery ID" value={info.current_battery_id || '—'} mono />
                <div className="v360-battery-block">
                  <div className="v360-battery-label">Battery health</div>
                  <div style={{display:'flex', alignItems:'center'}}>
                    <div className="v360-battery-meter">
                      <div className="v360-battery-fill" style={{ width: `${batteryHealth}%` }} />
                    </div>
                    <span className="v360-battery-value">{batteryHealth}%</span>
                  </div>

                </div>
              </section>

              {/* <section className="v360-panel">
                <h3>IDs</h3>
                <DetailRow label="Account" value={formatState(info.account)} />
                <DetailRow label="Partner Name" value={info.partner_name} mono />
                <DetailRow label="Partner ID" value={info.partner_id} mono />
                <DetailRow label="Station" value={info.station || selectedVehicle?.station || '—'} mono />
                <DetailRow label="Battery ID" value={info.current_battery_id || '—'} mono />
                <div className="v360-battery-block">
                  <div className="v360-battery-label">Battery health</div>
                  <div className="v360-battery-meter">
                    <div className="v360-battery-fill" style={{ width: `${batteryHealth}%` }} />
                  </div>
                  <span className="v360-battery-value">{batteryHealth}%</span>
                </div>
              </section> */}
            </div>
          )}

          {activeTab === 'vin-ticket-history' && (
            <div className="v360-table-card">
              <table className="v360-table">
                <thead>
                  <tr>
                    <th>Case ID</th>
                    <th>Issue</th>
                    <th>Status</th>
                    <th>Station</th>
                    <th>Created</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {vinTickets.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="v360-table-empty">No VIN ticket history found.</td>
                    </tr>
                  ) : vinTickets.map((ticket) => {
                    const created = formatDateTime(ticket.created_at, ticket.created_date, ticket.created_time)
                    return (
                      <tr key={ticket.ticket_id}>
                        <td className="linkish">{ticket.ticket_number}</td>
                        <td>{ticket.sub_category || ticket.category || '—'}</td>
                        <td>
                          <span className={`v360-badge ${formatStatusClass(ticket.status)}`}>{formatTicketStatus(ticket.status)}</span>
                        </td>
                        <td>{ticket.station_code || '—'}</td>
                        <td>{`${created.date} · ${created.time}`}</td>
                        <td>
                          <button className="v360-action-btn" onClick={() => openTicket(ticket)}>View</button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'rider-ticket-history' && (
            <div className="v360-table-card">
              <table className="v360-table">
                <thead>
                  <tr>
                    <th>Case ID</th>
                    <th>Issue</th>
                    <th>Status</th>
                    <th>Station</th>
                    <th>Created</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {riderTickets.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="v360-table-empty">No rider ticket history found.</td>
                    </tr>
                  ) : riderTickets.map((ticket) => {
                    const created = formatDateTime(ticket.created_at, ticket.created_date, ticket.created_time)
                    return (
                      <tr key={ticket.ticket_id}>
                        <td className="linkish">{ticket.ticket_number}</td>
                        <td>{ticket.sub_category || ticket.category || '—'}</td>
                        <td>
                          <span className={`v360-badge ${formatStatusClass(ticket.status)}`}>{formatTicketStatus(ticket.status)}</span>
                        </td>
                        <td>{ticket.station_code || '—'}</td>
                        <td>{`${created.date} · ${created.time}`}</td>
                        <td>
                          <button className="v360-action-btn" onClick={() => openTicket(ticket)}>View</button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'swap-history' && (
            <div className="v360-table-card">
              <table className="v360-table">
                <thead>
                  <tr>
                    <th>Transaction ID</th>
                    <th>Station ID</th>
                    <th>Vehicle ID</th>
                    <th>Swap Start</th>
                    <th>End / Duration</th>
                    <th>Swap Type</th>
                    <th>Vehicle Type</th>
                    <th>Status</th>
                    <th>Status Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {swapHistory.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="v360-table-empty">Swap history API is not available from the current backend response yet.</td>
                    </tr>
                  ) : swapHistory.map((entry) => {
                    const start = formatDateTime(entry.swap_start_at)
                    const end = formatDateTime(entry.swap_end_at)
                    return (
                      <tr key={entry.id}>
                        <td className="linkish">{entry.transaction_id}</td>
                        <td>{entry.station_id || '—'}</td>
                        <td>{entry.vehicle_id || '—'}</td>
                        <td>{entry.swap_start_at ? `${start.date} · ${start.time}` : '—'}</td>
                        <td>{entry.swap_end_at ? `${end.date} · ${end.time} · ${entry.duration}` : entry.duration || '—'}</td>
                        <td>{formatSwapType(entry.swap_type)}</td>
                        <td>{entry.vehicle_type || '—'}</td>
                        <td>
                          <span className={`v360-badge ${formatStatusClass(entry.status)}`}>{formatState(entry.status)}</span>
                        </td>
                        <td>
                          <div className="v360-reason-code">{entry.status_reason_code || '—'}</div>
                          <div className="v360-reason-copy">{entry.status_reason || '—'}</div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}
